import Skeleton from '@mui/material/Skeleton';
import moment from 'moment';
import { type JSX, useEffect, useMemo, useState } from 'react';

import { GroupedBarsChart } from 'Components/BaseCharts/GroupedBarsChart';
import { CSSColors } from 'Components/Colors';
import { FormFieldDateOptionSelect } from 'Components/FormField/FormFieldDateOptionSelect/FormFieldDateOptionSelect';
import { ChangeEventType } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldSelectDark } from 'Components/FormField/FormFieldSelectDark/FormFieldSelectDark';
import { CustomDateRangeModal } from 'Components/Modal/CustomDateRangeModal/CustomDateRangeModal';
import { Text } from 'Components/Text/Text';
import { toTitleCase } from 'Helpers/StringUtils';
import { useDateFilter } from 'Hooks/DateFilter';
import { DateOptionSelectOption, DefaultPastDateRangeOptions, IssuesExceptionsDashboardType } from 'Models/Dashboards';
import { ExceptionResponse, ExceptionRiskScore, ExceptionRiskScoreFilter, ExceptionRiskScoreFilterOptions, ExceptionStatus, isValidExceptionRiskScoreFilter } from 'Models/Exceptions';
import { OrganizationResponseBase } from 'Models/Organization';
import { countByMonth, countClosedExceptionsByMonth, countTotalExceptionsByMonth, getClosedExceptionsPerMonth, getMonths, getProductModuleForDashboardType } from 'Pages/Dashboards/Helpers/Dashboards.helpers';

import styles from './DashboardsHistoricalExceptions.module.css';
import { ClosedExceptionModalDisplayType, HistoricalExceptionsDisplayModal } from './HistoricalExceptionsDisplayModal/HistoricalExceptionsDisplayModal';

export interface DashboardsHistoricalExceptionsProps {
    allExceptions?: ExceptionResponse[];
    dashboardType: IssuesExceptionsDashboardType;
    exceptionsError?: string;
    organizations?: OrganizationResponseBase[];
    organizationsError?: string;
}

enum Modals {
    NONE,
    DISPLAY_CLOSED_EXCEPTIONS, // TODO: rename this. It's not just for closed exceptions.
    CUSTOM_DATE,
}

/**
 * Renders a bar-and-line chart that displays, over a given time period, and grouped by month:
 * * The number of exceptions opened
 * * The number of exceptions closed
 * * The total number of exceptions in the system
 *
 * TODO: rename this. It's not just for closed exceptions.
 */
export const DashboardsHistoricalExceptions = (props: DashboardsHistoricalExceptionsProps): JSX.Element => {
    const dashboardExceptions = useMemo(() => {
        if (!props.allExceptions) return undefined;
        const moduleType = getProductModuleForDashboardType(props.dashboardType);
        return props.allExceptions.filter((exception) => exception.type === moduleType && exception.status !== ExceptionStatus.DRAFT_OPEN);
    }, [props.allExceptions, props.dashboardType]);

    const [closedExceptions, setClosedExceptions] = useState<number[]>();
    const [totalOpenExceptions, setTotalOpenExceptions] = useState<number[]>();
    const [openedExceptions, setOpenedExceptions] = useState<number[]>();
    const [monthLabel, setMonthLabel] = useState<string[]>();
    const [clickedExceptions, setClickedExceptions] = useState<ExceptionResponse[]>();
    const [modalState, setModalState] = useState<Modals>(Modals.NONE);
    const [clickedDate, setClickedDate] = useState<string>();
    const [modalDisplayType, setModalDisplayType] = useState<ClosedExceptionModalDisplayType>();
    const [exceptionsFilterType, setExceptionsFilterType] = useState<ExceptionRiskScoreFilter>(ExceptionRiskScoreFilter.ALL);

    const dateFilterHookValues = useDateFilter(DateOptionSelectOption.LAST_THREE_MONTHS);

    useEffect(() => {
        const exceptionPriorityFilter = (exception: ExceptionResponse): boolean => {
            if (exceptionsFilterType === ExceptionRiskScoreFilter.ALL) {
                return true;
            } else if (exceptionsFilterType === ExceptionRiskScoreFilter.HIGH) {
                return exception.risk_score === ExceptionRiskScore.HIGH;
            } else if (exceptionsFilterType === ExceptionRiskScoreFilter.MODERATE_HIGH) {
                return exception.risk_score === ExceptionRiskScore.MODERATE_HIGH;
            } else if (exceptionsFilterType === ExceptionRiskScoreFilter.MODERATE) {
                return exception.risk_score === ExceptionRiskScore.MODERATE;
            } else if (exceptionsFilterType === ExceptionRiskScoreFilter.LOW_MODERATE) {
                return exception.risk_score === ExceptionRiskScore.LOW_MODERATE;
            } else if (exceptionsFilterType === ExceptionRiskScoreFilter.LOW) {
                return exception.risk_score === ExceptionRiskScore.LOW;
            } else {
                return true;
            }
        };
        if (dashboardExceptions) {
            const dashboardExceptionsByPriority = dashboardExceptions.filter(exceptionPriorityFilter);
            const allClosedExceptions = dashboardExceptionsByPriority.filter((exception) => exception.status === ExceptionStatus.CLOSED);

            const months = getMonths(dateFilterHookValues.selection.dateRange.startDate, dateFilterHookValues.selection.dateRange.endDate);

            const openedExceptions = countByMonth(dashboardExceptionsByPriority, months);
            const exceptionsClosedByMonth = countClosedExceptionsByMonth(allClosedExceptions, months);
            const totalExceptions = countTotalExceptionsByMonth(dashboardExceptionsByPriority, months);

            setClosedExceptions(exceptionsClosedByMonth);
            setOpenedExceptions(openedExceptions);
            setMonthLabel(months);
            setTotalOpenExceptions(totalExceptions);
        }
    }, [dashboardExceptions, exceptionsFilterType, dateFilterHookValues]);

    const handleChartClick = (item: string) => {
        const currentFilteredExceptions = dashboardExceptions?.filter((exception) => {
            if (exceptionsFilterType === ExceptionRiskScoreFilter.ALL) {
                return true;
            } else if (exceptionsFilterType === ExceptionRiskScoreFilter.HIGH) {
                return exception.risk_score === ExceptionRiskScore.HIGH;
            } else if (exceptionsFilterType === ExceptionRiskScoreFilter.MODERATE_HIGH) {
                return exception.risk_score === ExceptionRiskScore.MODERATE_HIGH;
            } else if (exceptionsFilterType === ExceptionRiskScoreFilter.MODERATE) {
                return exception.risk_score === ExceptionRiskScore.MODERATE;
            } else if (exceptionsFilterType === ExceptionRiskScoreFilter.LOW_MODERATE) {
                return exception.risk_score === ExceptionRiskScore.LOW_MODERATE;
            } else if (exceptionsFilterType === ExceptionRiskScoreFilter.LOW) {
                return exception.risk_score === ExceptionRiskScore.LOW;
            } else {
                return true;
            }
        });

        const clickedItem = item.split(' | ');
        const clickedDate = toTitleCase(clickedItem[0]);
        if (clickedItem[1] === 'Total Closed' && currentFilteredExceptions) {
            const allClosedExceptions = currentFilteredExceptions.filter((exception) => exception.status === ExceptionStatus.CLOSED);
            setClickedExceptions(getClosedExceptionsPerMonth(allClosedExceptions, clickedDate));
            setClickedDate(clickedDate);
            setModalState(Modals.DISPLAY_CLOSED_EXCEPTIONS);
            setModalDisplayType(ClosedExceptionModalDisplayType.CLOSED);
        } else if (clickedItem[1] === 'Total Opened' && currentFilteredExceptions) {
            const exceptions = currentFilteredExceptions.filter((exception) => moment(exception.created_timestamp).format('MMM YY') === clickedDate);
            setClickedExceptions(exceptions);
            setClickedDate(clickedDate);
            setModalState(Modals.DISPLAY_CLOSED_EXCEPTIONS);
            setModalDisplayType(ClosedExceptionModalDisplayType.OPEN);
        }
    };

    const handleRiskTypeChange = (value: ChangeEventType, formFieldId: string) => {
        if (isValidExceptionRiskScoreFilter(value)) {
            setExceptionsFilterType(value as ExceptionRiskScoreFilter);
        }
    };

    if (props.exceptionsError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Exception History
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {props.exceptionsError}
                </Text>
            </>
        );
    }

    if (props.organizationsError) {
        return (
            <>
                <div className={styles.cellHeader}>
                    <Text variant="Header2" color="white">
                        Exception History
                    </Text>
                </div>
                <Text color="white" variant="Header4">
                    {props.organizationsError}
                </Text>
            </>
        );
    }

    return (
        <>
            {modalState === Modals.CUSTOM_DATE && <CustomDateRangeModal datesRestriction="pastDatesOnly" hideModal={() => setModalState(Modals.NONE)} dateFilterHookValues={dateFilterHookValues} />}
            {modalState === Modals.DISPLAY_CLOSED_EXCEPTIONS && clickedDate && clickedExceptions && modalDisplayType !== undefined && <HistoricalExceptionsDisplayModal selectedException={clickedExceptions} date={clickedDate} hideModal={() => setModalState(Modals.NONE)} modalType={modalDisplayType} dashboardType={props.dashboardType} organizations={props.organizations} />}
            <div className={styles.cellHeader}>
                <Text variant="Header2" color="white">
                    Exception History
                </Text>
                <div className={styles.filters}>
                    <FormFieldSelectDark formFieldId="exceptionRiskScore" formFieldLabel="Risk Score..." options={ExceptionRiskScoreFilterOptions} selectedOption={exceptionsFilterType} handleChange={handleRiskTypeChange} />
                    <FormFieldDateOptionSelect formFieldId="date" options={DefaultPastDateRangeOptions} onSelectCustom={() => setModalState(Modals.CUSTOM_DATE)} dateFilterHookValues={dateFilterHookValues} />
                </div>
            </div>
            {closedExceptions && monthLabel && totalOpenExceptions && openedExceptions ? (
                <GroupedBarsChart
                    onChartClick={handleChartClick}
                    xAxisLabels={monthLabel}
                    line={{ name: 'Total Exceptions', color: CSSColors.YELLOW, data: totalOpenExceptions }}
                    barsSeriesArray={[
                        { name: 'Total Opened', color: CSSColors.LIGHT_BLUE, data: openedExceptions },
                        { name: 'Total Closed', color: CSSColors.GRAY, data: closedExceptions },
                    ]}
                    yAxisInfo={[
                        { name: 'Total Closed/Opened', position: 'left', minInterval: 1 },
                        { name: 'Total Exceptions', position: 'right', minInterval: 1 },
                    ]}
                    groupingMode="clustered"
                />
            ) : (
                <Skeleton variant="rounded" width={'100%'} height={300} />
            )}
        </>
    );
};
